/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { gql } from 'graphql-request'


export const NAVIGATION_QUERY = gql`
  query GetNavigation {
    navigation {
      documentId
      menuItems {
        __typename
        ... on ComponentUtilsPageLink {
          id
          title
          relatedPage {
            slug
            title
          }
        }
        ... on ComponentUtilsExternalLink {
          id
          text
          url
        }
      }
    }
  }
`

export const GLOBAL_QUERY = gql`
  query GetGlobal {
    global {
      documentId
      siteName
      siteDescription
      favicon {
        url
        alternativeText
      }
      socialLinks {
        platform
        url
      }
      Offices {
        id
        title
        street
        postcode
        town
        country
        timezone
        email {
          id
          email
        }
        Map {
          id
          asset {
            url
            alternativeText
          }
          caption
        }
      }
      websitePassword {
        passwordProtected
        password
      }
    }
  }
`



export const HOMEPAGE_QUERY = gql`
  query GetHomepage {
    homepage {
      documentId
      Style {
        id
        classNames
        theme
        css
        pageTopSpacer
      }
      cover {
        id
        tagline
        logo
        background {
          videoId
          poster {
            url
            alternativeText
            width
            height
            formats
          }
        }
      }
      cards {
        title
        Size {
          size
          sizeMobile
          height
          heightMobile
        }
        card {
          id
          heading
          description
          blockAnchorID
          thumb {
            url
            alternativeText
          }
          link {
            text
            slug
            anchorID
          }
        }
      }
      about {
        id
        title
        description
        textExtra
        button {
          style {
            buttonStyle
          }
          link {
            text
            slug
            anchorID
          }
          show {
            display
          }
          align {
            align
          }
        }
        profession {
          text
          slug
          anchorID
        }
      }
      video {
        video {
          videoId
          videoSource
          poster {
            url
            alternativeText
          }
          videoRatio
        }
        settings {
          autoPlay
          mute
          loop
          showControls
        }
        size {
          size
          sizeMobile
          height
          heightMobile
        }
      }
    }
  }
`

// Block fragments with fixed parameter syntax
const BLOCK_FRAGMENTS = gql`
  fragment BlockContent on PageContentDynamicZone {
    __typename
    ... on ComponentBlocksTextBlock {
      id
      title
      body
      blockAnchorID
      button {
        id
        style {
          buttonStyle
        }
        link {
          text
          slug
          anchorID
        }
        show {
          display
        }
        align {
          align
        }
      }
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksTextAndImageBlock {
      id
      media {
        asset {
          url
          alternativeText
        }
        caption
      }
      mediaPosition {
        align
      }
      blockAnchorID
      text {
        title
        body
        button {
          id
          style {
            buttonStyle
          }
          link {
            text
            slug
            anchorID
          }
          show {
            display
          }
          align {
            align
          }
        }
      }
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksTwoTextsBlock {
      id
      title
      textLeft
      textRight
      blockAnchorID
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksImageBlock {
      id
      image {
        asset {
          url
          alternativeText
        }
        caption
      }
      lightbox
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksVideoBlock {
      id
      video {
        videoId
        videoSource
        poster {
          url
          alternativeText
        }
        videoRatio
      }
      settings {
        autoPlay
        mute
        loop
        showControls
      }
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksTableBlock {
      id
      title
      row(pagination: { limit: 100 }) {
        heading
        description
        foot
      }
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksMapBlock {
      id
      title
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksCoverBlock {
      id
      logo
      tagline
      background {
        videoId
        videoSource
        poster {
          url
          alternativeText
        }
        videoRatio
      }
    }
    ... on ComponentBlocksCardsGridBlock {
      id
      title
      Size {
        size
        sizeMobile
        height
        heightMobile
      }
      card(pagination: { limit: 100 }) {
        id
        heading
        description
        blockAnchorID
        thumb {
          url
          alternativeText
        }
        link {
          text
          slug
          anchorID
        }
      }
    }
    ... on ComponentBlocksCarouselBlock {
      id
      media {
        media(pagination: { limit: 100 }) {
          asset {
            url
            alternativeText
          }
          caption
        }
      }
      imageGroupStyle
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksButtonBlock {
      id
      style {
        buttonStyle
      }
      link {
        text
        slug
        anchorID
      }
      show {
        display
      }
      align {
        align
      }
      blockAnchorID
    }
  }
`

export const ABOUT_QUERY = gql`
  query GetAbout {
    pages(filters: { slug: { eq: "about" } }) {
      documentId
      title
      slug
      pageStyle {
        id
        classNames
        theme
        css
        pageTopSpacer
      }
      password {
        passwordProtected
        password
      }
      seo {
        seoTitle
        seoDescription
        canonicalUrl
        noindex
        nofollow
        structuredData
      }
      socials {
        ogTitle
        ogDescription
        ogImage {
          url
          alternativeText
        }
        twitterCard
        twitterTitle
        twitterDescription
        twitterImage {
          url
          alternativeText
        }
      }
      featuredImage {
        asset {
          url
          alternativeText
        }
        caption
      }
      content {
        ...BlockContent
      }
    }
  }
  ${BLOCK_FRAGMENTS}
`

export const CONTACT_QUERY = gql`
  query GetContact {
    pages(filters: { slug: { eq: "contact" } }) {
      documentId
      title
      slug
      pageStyle {
        id
        classNames
        theme
        css
        pageTopSpacer
      }
      password {
        passwordProtected
        password
      }
      seo {
        seoTitle
        seoDescription
        canonicalUrl
        noindex
        nofollow
        structuredData
      }
      socials {
        ogTitle
        ogDescription
        ogImage {
          url
          alternativeText
        }
        twitterCard
        twitterTitle
        twitterDescription
        twitterImage {
          url
          alternativeText
        }
      }
      featuredImage {
        asset {
          url
          alternativeText
        }
        caption
      }
      content {
        ...BlockContent
      }
    }
  }
  ${BLOCK_FRAGMENTS}
`

export const DESIGN_QUERY = gql`
  query GetDesignNew {
    pages(filters: { slug: { eq: "design" } }) {
      documentId
      title
      slug
      pageStyle {
        id
        classNames
        theme
        css
        pageTopSpacer
      }
      password {
        passwordProtected
        password
      }
      content {
        ...BlockContent
      }
    }
  }
  ${BLOCK_FRAGMENTS}
`

export const TECHNOLOGY_QUERY = gql`
  query GetTechnologyNew {
    pages(filters: { slug: { eq: "technology" } }) {
      documentId
      title
      slug
      pageStyle {
        id
        classNames
        theme
        css
        pageTopSpacer
      }
      password {
        passwordProtected
        password
      }
      content {
        ...BlockContent
      }
    }
  }
  ${BLOCK_FRAGMENTS}
`

export const PAGE_QUERY = gql`
  query GetPage($slug: String!) {
    pages(filters: { slug: { eq: $slug } }) {
      documentId
      title
      slug
      pageStyle {
        id
        classNames
        theme
        css
        pageTopSpacer
      }
      password {
        passwordProtected
        password
      }
      seo {
        seoTitle
        seoDescription
        canonicalUrl
        noindex
        nofollow
        structuredData
      }
      socials {
        ogTitle
        ogDescription
        ogImage {
          url
          alternativeText
        }
        twitterCard
        twitterTitle
        twitterDescription
        twitterImage {
          url
          alternativeText
        }
      }
      featuredImage {
        asset {
          url
          alternativeText
        }
        caption
      }
      content {
        ...BlockContent
      }
    }
  }
  ${BLOCK_FRAGMENTS}
`

export const POSTS_QUERY = gql`
  query GetPosts {
    posts(sort: "createdAt:desc") {
      documentId
      title
      slug
      pageStyle {
        id
        classNames
        theme
        css
        pageTopSpacer
      }
      thumbnail {
        url
        alternativeText
        caption
      }
      featuredImage {
        asset {
          url
          alternativeText
        }
        caption
      }
      newsPublishedDate
      createdAt
      updatedAt
      publishedAt
    }
  }
`

export const ALL_POSTS_QUERY = gql`
  query GetAllPosts {
    posts(pagination: { limit: 1000 }, sort: "createdAt:desc") {
      documentId
      title
      slug
      pageStyle {
        id
        classNames
        theme
        css
        pageTopSpacer
      }
      thumbnail {
        url
        alternativeText
        caption
      }
      featuredImage {
        asset {
          url
          alternativeText
        }
        caption
      }
      newsPublishedDate
      createdAt
      updatedAt
      publishedAt
    }
  }
`

export const POSTS_PAGINATED_QUERY = gql`
  query GetPostsPaginated($page: Int!, $pageSize: Int!) {
    posts_connection(
      sort: "createdAt:desc",
      pagination: { page: $page, pageSize: $pageSize }
    ) {
      nodes {
        documentId
        title
        slug
        pageStyle {
          id
          classNames
          theme
          css
          pageTopSpacer
        }
        thumbnail {
          url
          alternativeText
          caption
        }
        featuredImage {
          asset {
            url
            alternativeText
          }
          caption
        }
        newsPublishedDate
        createdAt
        updatedAt
        publishedAt
      }
      pageInfo {
        total
        page
        pageSize
        pageCount
      }
    }
  }
`

export const NEWS_LIST_QUERY = gql`
  query GetNewsList {
    pages(filters: { slug: { eq: "news" } }) {
      documentId
      title
      slug
      pageStyle {
        id
        classNames
        theme
        css
        pageTopSpacer
      }
      password {
        passwordProtected
        password
      }
      seo {
        seoTitle
        seoDescription
        canonicalUrl
        noindex
        nofollow
        structuredData
      }
      socials {
        ogTitle
        ogDescription
        ogImage {
          url
          alternativeText
        }
        twitterCard
        twitterTitle
        twitterDescription
        twitterImage {
          url
          alternativeText
        }
      }
    }
  }
`

// Post fragments with fixed parameter syntax for posts
const POST_BLOCK_FRAGMENTS = gql`
  fragment PostBlockContent on PostItemsDynamicZone {
    __typename
    ... on ComponentBlocksTextBlock {
      id
      title
      body
      blockAnchorID
      button {
        id
        style {
          buttonStyle
        }
        link {
          text
          slug
          anchorID
        }
        show {
          display
        }
        align {
          align
        }
      }
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksTextAndImageBlock {
      id
      media {
        asset {
          url
          alternativeText
        }
        caption
      }
      mediaPosition {
        align
      }
      blockAnchorID
      text {
        title
        body
        button {
          id
          style {
            buttonStyle
          }
          link {
            text
            slug
            anchorID
          }
          show {
            display
          }
          align {
            align
          }
        }
      }
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksImageBlock {
      id
      image {
        asset {
          url
          alternativeText
        }
        caption
      }
      lightbox
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksVideoBlock {
      id
      video {
        videoId
        videoSource
        poster {
          url
          alternativeText
        }
        videoRatio
      }
      settings {
        autoPlay
        mute
        loop
        showControls
      }
      size {
        size
        sizeMobile
        height
        heightMobile
      }
    }
    ... on ComponentBlocksButtonBlock {
      id
      style {
        buttonStyle
      }
      link {
        text
        slug
        anchorID
      }
      show {
        display
      }
      align {
        align
      }
    }
    ... on ComponentBlocksCarouselBlock {
      id
      media {
        media(pagination: { limit: 100 }) {
          asset {
            url
            alternativeText
          }
          caption
        }
      }
      imageGroupStyle
    }
  }
`

export const SINGLE_POST_QUERY = gql`
  query GetSinglePost($slug: String!) {
    posts(filters: { slug: { eq: $slug } }) {
      documentId
      title
      slug
      pageStyle {
        id
        classNames
        theme
        css
        pageTopSpacer
      }
      thumbnail {
        url
        alternativeText
        caption
      }
      featuredImage {
        asset {
          url
          alternativeText
        }
        caption
      }
      seo {
        seoTitle
        seoDescription
        canonicalUrl
        noindex
        nofollow
        structuredData
      }
      socials {
        ogTitle
        ogDescription
        ogImage {
          url
          alternativeText
        }
        twitterCard
        twitterTitle
        twitterDescription
        twitterImage {
          url
          alternativeText
        }
      }
      newsPublishedDate
      createdAt
      updatedAt
      publishedAt
      items {
        ...PostBlockContent
      }
    }
  }
  ${POST_BLOCK_FRAGMENTS}
`

export const ADJACENT_POSTS_QUERY = gql`
  query GetAdjacentPosts($currentSlug: String!) {
    # Get current post to find its creation date
    currentPost: posts(filters: { slug: { eq: $currentSlug } }) {
      documentId
      createdAt
    }
    
    # Get previous post (older than current)
    previousPost: posts(
      filters: { 
        slug: { ne: $currentSlug }
        createdAt: { lt: "PLACEHOLDER_DATE" }
      }
      sort: "createdAt:desc"
      pagination: { limit: 1 }
    ) {
      documentId
      title
      slug
      thumbnail {
        url
        alternativeText
        caption
      }
      featuredImage {
        asset {
          url
          alternativeText
        }
        caption
      }
      createdAt
    }
    
    # Get next post (newer than current)
    nextPost: posts(
      filters: { 
        slug: { ne: $currentSlug }
        createdAt: { gt: "PLACEHOLDER_DATE" }
      }
      sort: "createdAt:asc"
      pagination: { limit: 1 }
    ) {
      documentId
      title
      slug
      thumbnail {
        url
        alternativeText
        caption
      }
      featuredImage {
        asset {
          url
          alternativeText
        }
        caption
      }
      createdAt
    }
  }
`

export const SEO_QUERY = gql`
  query GetSeo {
    seo {
      metaSEO {
        seoTitle
        seoDescription
        canonicalUrl
        noindex
        nofollow
        structuredData
      }
      metaSocials {
        ogTitle
        ogDescription
        ogImage {
          url
          alternativeText
        }
        twitterCard
        twitterTitle
        twitterDescription
        twitterImage {
          url
          alternativeText
        }
      }
    }
  }
` 